import { useState } from "react";
import { toast } from "sonner";
import { logError } from "../utils/logger";

export const usePropertyManagement = (dashboardApi, developerDashboardApi, setLoading) => {
  const [properties, setProperties] = useState([]);
  const [deletedProperties, setDeletedProperties] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    archived: 0,
    forSale: 0,
    forRent: 0,
    presale: 0,
    both: 0,
    totalViews: 0,
    totalFavorites: 0,
    totalContacts: 0,
  });

  const fetchPropertyStats = async () => {
    try {
      const r = await dashboardApi.getPropertyStats();
      if (r.ok) {
        const d = await r.json();
        setStats((prev) => ({
          ...prev,
          totalFavorites: d.total_favorites || 0,
          totalContacts: d.total_contacts || 0,
        }));
      }
    } catch (e) {
      logError("usePropertyManagement.fetchPropertyStats", e);
    }
  };

  const fetchUserProperties = async () => {
    try {
      const r = await dashboardApi.getUserProperties();
      const data = await r.json();
      setProperties(data);
      const totalViews = data.reduce((sum, p) => sum + (p.view_count || 0), 0);
      setStats({
        total: data.length,
        active: data.filter((p) => p.is_active !== false).length,
        archived: data.filter((p) => p.is_active === false).length,
        forSale: data.filter((p) =>
          (p.operation_types || [p.status]).includes("venta"),
        ).length,
        forRent: data.filter((p) =>
          (p.operation_types || [p.status]).includes("renta"),
        ).length,
        presale: data.filter((p) =>
          (p.operation_types || [p.status]).includes("preventa"),
        ).length,
        both: 0,
        totalViews,
        totalFavorites: 0,
        totalContacts: 0,
      });
      fetchPropertyStats();
    } catch (e) {
      logError("usePropertyManagement.fetchUserProperties", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedProperties = async () => {
    try {
      const r = await dashboardApi.getDeletedUserProperties();
      if (r.ok) setDeletedProperties(await r.json());
    } catch (e) {
      logError("usePropertyManagement.fetchDeletedProperties", e);
    }
  };

  const handleDeleteProperty = async (id) => {
    if (!window.confirm("¿Eliminar esta propiedad?")) return;
    try {
      const r = await developerDashboardApi.deleteProperty(id);
      if (!r.ok) throw new Error();
      toast.success("Propiedad eliminada");
      fetchUserProperties();
      fetchDeletedProperties();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const handleRestoreProperty = async (id) => {
    try {
      const r = await developerDashboardApi.restoreProperty(id);
      if (!r.ok) throw new Error();
      toast.success("Propiedad restaurada");
      fetchUserProperties();
      fetchDeletedProperties();
    } catch (e) {
      toast.error("Error al restaurar");
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const r = await developerDashboardApi.toggleActiveProperty(id);
      if (!r.ok) throw new Error();
      const d = await r.json();
      toast.success(d.is_active ? "Propiedad activada" : "Propiedad archivada");
      fetchUserProperties();
      fetchDeletedProperties();
    } catch (e) {
      toast.error("Error");
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      const r = await developerDashboardApi.toggleFeaturedProperty(id);
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.detail);
      }
      const d = await r.json();
      toast.success(
        d.is_featured ? "Propiedad destacada" : "Destacado removido",
      );
      fetchUserProperties();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return {
    properties,
    deletedProperties,
    stats,
    fetchUserProperties,
    fetchDeletedProperties,
    fetchPropertyStats,
    handleDeleteProperty,
    handleRestoreProperty,
    handleToggleActive,
    handleToggleFeatured,
  };
};
